import { Component, EventEmitter, Input, Output } from '@angular/core';
import { StatusColor, DatTimelineSegment, TimelineSegment } from '../../models/monitoring.models';
import { utcMinutesToParisLabel } from '../../utils/time-utils';

type RenderSegment = DatTimelineSegment & { clickable: boolean; selected?: boolean };

const SLOT_MINUTES = 15;

@Component({
  selector: 'app-horizontal-timeline',
  standalone: true,
  template: `
    <div class="timeline-wrap" [style.--slot-count]="axisTicks.length">
      <div class="timeline-track" [class.split]="splitIntoSlots">
        @for (seg of displaySegments; track trackSegment(seg, $index)) {
          <button
            type="button"
            class="segment"
            [class]="seg.color"
            [class.selected]="seg.selected"
            [style.flex]="segmentFlex(seg)"
            [title]="seg.startLabel + ' - ' + seg.endLabel"
            [disabled]="!seg.clickable"
            (click)="onSegmentClick(seg)"
          >
            @if (showLabels && seg.color !== 'green' && !splitIntoSlots) {
              <span class="seg-label">{{ seg.startLabel }}</span>
            }
          </button>
        }
      </div>
      @if (splitIntoSlots) {
        <div class="axis axis-slots">
          @for (tick of axisTicks; track tick.minutes) {
            <span [class.tick-selected]="tick.selected">{{ tick.label }}</span>
          }
        </div>
      } @else {
        <div class="axis">
          <span>{{ windowStartLabel }}</span>
          <span class="axis-center">{{ centerLabel }}</span>
          <span>{{ windowEndLabel }}</span>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .timeline-wrap {
        width: 100%;
      }

      .timeline-track {
        display: flex;
        height: 2rem;
        border-radius: 999px;
        overflow: hidden;
        background: #43a047;
      }

      .timeline-track.split {
        border-radius: 6px;
        gap: 0;
      }

      .timeline-track.split .segment {
        border-radius: 0;
        border-right: 2px solid rgba(255, 255, 255, 0.55);
      }

      .timeline-track.split .segment:last-child {
        border-right: none;
      }

      .segment {
        border: none;
        padding: 0;
        margin: 0;
        min-width: 2px;
        cursor: pointer;
        position: relative;
        transition: filter 0.15s;
      }

      .segment.selected {
        outline: 3px solid #1565c0;
        outline-offset: -3px;
        z-index: 2;
      }

      .segment:not(:disabled):hover {
        filter: brightness(1.08);
      }

      .segment.green {
        background: #43a047;
      }
      .segment.yellow,
      .segment.orange {
        background: #fb8c00;
      }
      .segment.red {
        background: #e53935;
      }
      .segment.grey {
        background: #bdbdbd;
      }

      .seg-label {
        position: absolute;
        top: -1.35rem;
        left: 50%;
        transform: translateX(-50%);
        font-size: 0.75rem;
        color: #333;
        white-space: nowrap;
        pointer-events: none;
      }

      .axis {
        display: flex;
        justify-content: space-between;
        margin-top: 0.35rem;
        font-size: 0.8rem;
        color: #555;
        padding: 0 0.15rem;
      }

      .axis-slots {
        display: grid;
        grid-template-columns: repeat(var(--slot-count, 8), 1fr);
        gap: 0;
        font-size: 0.65rem;
        text-align: center;
        padding: 0;
      }

      .axis-slots span {
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .tick-selected {
        font-weight: 700;
        color: #1565c0;
      }

      .axis-center {
        font-weight: 700;
        color: #1565c0;
      }
    `,
  ],
})
export class HorizontalTimelineComponent {
  @Input({ required: true }) segments!: DatTimelineSegment[];
  @Input({ required: true }) windowStartMinutes!: number;
  @Input({ required: true }) windowEndMinutes!: number;
  @Input() windowStartLabel = '';
  @Input() windowEndLabel = '';
  @Input() centerLabel = '';
  @Input() selectedMinutes: number | null = null;
  @Input() splitIntoSlots = false;
  @Input() showLabels = true;
  /**
   * Créneau (minutes UTC) au-delà duquel aucune donnée n'existe encore (l'heure courante,
   * ou null si la borne ne s'applique pas — ex. consultation d'une journée passée).
   * Les créneaux >= nowMinutes sont forcés en gris et non cliquables (cf. décalage de 2h,
   * ticket [DAT TER] IHM ASCT).
   */
  @Input() nowMinutes: number | null = null;

  @Output() segmentClick = new EventEmitter<DatTimelineSegment>();

  private static readonly fmt = (minutes: number): string => utcMinutesToParisLabel(minutes);

  get displaySegments(): RenderSegment[] {
    const continuous = this.buildContinuousSegments();
    if (!this.splitIntoSlots) {
      return continuous;
    }
    return this.splitInto15MinSlots(continuous);
  }

  get axisTicks(): { minutes: number; label: string; selected: boolean }[] {
    if (!this.splitIntoSlots) {
      return [];
    }
    const ticks: { minutes: number; label: string; selected: boolean }[] = [];
    let t = this.alignSlotStart(this.windowStartMinutes);
    while (t < this.windowEndMinutes) {
      ticks.push({
        minutes: t,
        label: HorizontalTimelineComponent.fmt(t),
        selected: this.isSlotSelected(t, Math.min(t + SLOT_MINUTES, this.windowEndMinutes)),
      });
      t += SLOT_MINUTES;
    }
    return ticks;
  }

  private buildContinuousSegments(): RenderSegment[] {
    const windowStart = this.windowStartMinutes;
    const windowEnd = this.windowEndMinutes;

    const clipped = this.segments
      .map((seg): RenderSegment | null => {
        const originalStart = seg.startMinutes;
        const start = Math.max(seg.startMinutes, windowStart);
        const end = Math.min(seg.endMinutes, windowEnd);
        if (end <= start) {
          return null;
        }
        return {
          ...seg,
          startMinutes: start,
          endMinutes: end,
          startLabel: HorizontalTimelineComponent.fmt(start),
          endLabel: HorizontalTimelineComponent.fmt(end),
          sourceSegmentStart: seg.sourceSegmentStart ?? originalStart,
          clickable: !!seg.detail,
        };
      })
      .filter((s): s is RenderSegment => s !== null)
      .sort((a, b) => a.startMinutes - b.startMinutes);

    return this.fillGaps(clipped, windowStart, windowEnd);
  }

  private splitInto15MinSlots(continuous: RenderSegment[]): RenderSegment[] {
    const slots: RenderSegment[] = [];
    let t = this.alignSlotStart(this.windowStartMinutes);

    while (t < this.windowEndMinutes) {
      const slotEnd = Math.min(t + SLOT_MINUTES, this.windowEndMinutes);
      // Créneau pas encore arrivé (aucun fichier back ne peut exister pour ce quart d'heure) :
      // gris et inaccessible, quoi que dise le back — évite le décalage de 2h en vue détaillée.
      const isFuture = this.nowMinutes != null && t >= this.nowMinutes;

      if (isFuture) {
        slots.push({
          startMinutes: t,
          endMinutes: slotEnd,
          startLabel: HorizontalTimelineComponent.fmt(t),
          endLabel: HorizontalTimelineComponent.fmt(slotEnd),
          color: 'grey',
          clickable: false,
          selected: this.isSlotSelected(t, slotEnd),
        });
        t += SLOT_MINUTES;
        continue;
      }

      const overlapping = continuous.filter(
        (s) => s.startMinutes <= slotEnd && s.endMinutes > t,
      );
      const dominant = this.dominantSegment(overlapping, t, slotEnd);
      const detailSeg = overlapping.find((s) => s.detail);
      const sourceStart = detailSeg?.sourceSegmentStart ?? detailSeg?.startMinutes;

      slots.push({
        startMinutes: t,
        endMinutes: slotEnd,
        startLabel: HorizontalTimelineComponent.fmt(t),
        endLabel: HorizontalTimelineComponent.fmt(slotEnd),
        color: dominant?.color ?? 'green',
        detail: detailSeg?.detail,
        sourceSegmentStart: sourceStart,
        clickable: !!detailSeg?.detail,
        selected: this.isSlotSelected(t, slotEnd),
      });

      t += SLOT_MINUTES;
    }

    return slots;
  }

  private dominantSegment(
    segments: RenderSegment[],
    slotStart: number,
    slotEnd: number,
  ): RenderSegment | undefined {
    if (segments.length === 0) {
      return undefined;
    }
    const priority: StatusColor[] = ['red', 'orange', 'yellow', 'green', 'grey'];
    // Pire couleur sur la plage : toute anomalie pendant le créneau compte.
    for (const color of priority) {
      const match = segments.find((s) => s.color === color);
      if (match) {
        return match;
      }
    }

    return segments[0];
  }

  private isSlotSelected(slotStart: number, slotEnd: number): boolean {
    if (this.selectedMinutes == null) {
      return false;
    }
    const sel = this.selectedMinutes;
    return sel >= slotStart && sel < slotEnd;
  }

  private alignSlotStart(minutes: number): number {
    return Math.floor(minutes / SLOT_MINUTES) * SLOT_MINUTES;
  }

  private fillGaps(
    segments: RenderSegment[],
    windowStart: number,
    windowEnd: number,
  ): RenderSegment[] {
    const result: RenderSegment[] = [];
    let cursor = windowStart;

    for (const seg of segments) {
      if (seg.startMinutes > cursor) {
        result.push(this.greenSegment(cursor, seg.startMinutes));
      }
      result.push(seg);
      cursor = Math.max(cursor, seg.endMinutes);
    }

    if (cursor < windowEnd) {
      result.push(this.greenSegment(cursor, windowEnd));
    }

    return result.length > 0 ? result : [this.greenSegment(windowStart, windowEnd)];
  }

  private greenSegment(start: number, end: number): RenderSegment {
    return {
      startMinutes: start,
      endMinutes: end,
      startLabel: HorizontalTimelineComponent.fmt(start),
      endLabel: HorizontalTimelineComponent.fmt(end),
      color: 'green',
      clickable: false,
    };
  }

  segmentFlex(seg: TimelineSegment): number {
    const duration = this.windowEndMinutes - this.windowStartMinutes;
    return Math.max(0.02, (seg.endMinutes - seg.startMinutes) / duration);
  }

  trackSegment(seg: RenderSegment, index: number): string {
    return `${seg.startMinutes}-${seg.endMinutes}-${seg.color}-${index}`;
  }

  onSegmentClick(seg: RenderSegment): void {
    if (!seg.clickable || !seg.detail) {
      return;
    }
    this.segmentClick.emit({
      ...seg,
      // En mode créneaux 15 min, l'API attend le début du créneau cliqué (10h00),
      // pas l'horodatage interne du changement d'état (ex. 10h05).
      startMinutes: this.splitIntoSlots
        ? seg.startMinutes
        : (seg.sourceSegmentStart ?? seg.startMinutes),
    });
  }
}

@Component({
  selector: 'app-station-history-bar',
  standalone: true,
  template: `
    <button
      type="button"
      class="history-row"
      [class.selected]="selected"
      (click)="rowClick.emit()"
    >
      <span class="time">{{ time }}</span>
      <span class="bar" [class]="color"></span>
    </button>
  `,
  styles: [
    `
      .history-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        width: 100%;
        padding: 0.35rem 0;
        border: none;
        background: transparent;
        cursor: pointer;
        text-align: left;
      }

      .history-row.selected .bar {
        outline: 2px solid #1565c0;
        outline-offset: 2px;
      }

      .time {
        min-width: 3rem;
        font-size: 0.95rem;
        color: #333;
      }

      .bar {
        flex: 1;
        height: 1.25rem;
        border-radius: 999px;
      }

      .bar.green {
        background: #43a047;
      }
      .bar.yellow,
      .bar.orange {
        background: #fb8c00;
      }
      .bar.red {
        background: #e53935;
      }
      .bar.grey {
        background: #bdbdbd;
      }
    `,
  ],
})
export class StationHistoryBarComponent {
  @Input({ required: true }) time!: string;
  @Input({ required: true }) color!: StatusColor;
  @Input() selected = false;
  @Output() rowClick = new EventEmitter<void>();
}
